import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  Alert,
  CircularProgress,
  Link,
  InputAdornment,
  Divider,
  Chip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Email as EmailIcon, Badge as BadgeIcon } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import passwordAPI from "../utils/passwordAPI";

/**
 * Password Reset Request Component
 *
 * This component handles the first step of the password reset process,
 * allowing users to request a password reset email.
 */
const ForgotPassword = () => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [universityId, setUniversityId] = useState("");
  const [isEmailOption, setIsEmailOption] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // For the university ID method
  const [maskedEmail, setMaskedEmail] = useState("");
  const [foundUserId, setFoundUserId] = useState("");
  const [step, setStep] = useState(1); // Step 1: Enter ID, Step 2: Confirm email

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (isEmailOption) {
      // Direct email reset using Firebase Auth
      if (!email) {
        setError("Please enter your email address");
        return;
      }

      setIsLoading(true);
      try {
        await resetPassword(email);
        setSuccess("Password reset link sent! Check your email inbox.");
      } catch (err) {
        console.error("Error requesting password reset:", err);
        setError(
          err.message || "Failed to send password reset. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    } else {
      // University ID reset
      if (!universityId) {
        setError("Please enter your University ID");
        return;
      }

      setIsLoading(true);

      try {
        if (step === 1) {
          // Step 1: Look up the user by University ID
          const response = await passwordAPI.findByUniversityId(universityId);
          setMaskedEmail(response.data.maskedEmail);
          setFoundUserId(response.data.userId);
          setStep(2);
        } else {
          // Step 2: Send the reset email
          const response = await passwordAPI.resetByUniversityId(universityId);
          setSuccess(
            `Password reset link sent to ${response.data.maskedEmail}. Check your email inbox.`
          );
          // Reset the form
          setStep(1);
          setMaskedEmail("");
          setFoundUserId("");
        }
      } catch (err) {
        console.error("Error in University ID reset:", err);
        setError(
          err.response?.data?.message ||
            "Failed to process your request. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  const toggleResetOption = () => {
    setIsEmailOption(!isEmailOption);
    setError("");
    setSuccess("");
    setStep(1);
    setMaskedEmail("");
    setFoundUserId("");
  };

  const goBack = () => {
    if (step === 2) {
      setStep(1);
      setMaskedEmail("");
      setFoundUserId("");
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mb: 8,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: "100%",
            borderRadius: 2,
            background: "linear-gradient(to bottom right, #ffffff, #f5f5f5)",
          }}
        >
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Reset Password
          </Typography>

          <Divider sx={{ mb: 3 }}>
            <Chip label={isEmailOption ? "Email Address" : "University ID"} />
          </Divider>

          {!isEmailOption && (
            <Typography
              variant="body1"
              align="center"
              color="textSecondary"
              sx={{ mb: 3 }}
            >
              {step === 1
                ? "Enter your University ID and we'll help you reset your password"
                : `We found your account. A password reset link will be sent to ${maskedEmail}`}
            </Typography>
          )}

          {isEmailOption && (
            <Typography
              variant="body1"
              align="center"
              color="textSecondary"
              sx={{ mb: 3 }}
            >
              Enter your email address and we'll send you a link to reset your
              password.
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {isEmailOption ? (
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            ) : step === 1 ? (
              <TextField
                margin="normal"
                required
                fullWidth
                id="universityId"
                label="University ID"
                name="universityId"
                autoFocus
                value={universityId}
                onChange={(e) => setUniversityId(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon />
                    </InputAdornment>
                  ),
                }}
              />
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Confirm that you want to send a password reset link to{" "}
                {maskedEmail}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isLoading}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : isEmailOption ? (
                "Send Reset Link"
              ) : step === 1 ? (
                "Find My Account"
              ) : (
                "Send Reset Link"
              )}
            </Button>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {!isEmailOption && step === 2 ? (
                <Link component="button" variant="body2" onClick={goBack}>
                  Back
                </Link>
              ) : (
                <Link
                  component="button"
                  variant="body2"
                  onClick={toggleResetOption}
                >
                  Reset with {isEmailOption ? "University ID" : "Email"} instead
                </Link>
              )}

              <Link component={RouterLink} to="/login" variant="body2">
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
